import React, { useState, useEffect } from 'react';
import '../../styles/SubForm.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';

/**
 * SubForm: A reusable mini-form array component storing JSON in a single field.
 * @param {string} name - field name
 * @param {Array} value - array of objects
 * @param {Function} onChange(name, newValue)
 * @param {Array} fields - schema: [{ name, label, type, options?, width? }]
 * @param {string} addLabel - text for add button
 * @param {boolean} fixedRows - if true, prevents adding/removing rows
 */
const SubForm = ({ name, value = [], onChange, fields, fixedRows = false, disabled = false }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const base = Array.isArray(value) ? value : [];
    if (fixedRows) {
      // For fixed rows (like billing items), don't add empty row
      setRows(base);
    } else {
      // For dynamic rows, add empty row at the end
      setRows([...base, fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {})]);
    }
  }, [value, fields, fixedRows]);

  const triggerChange = (updated) => {
    const filtered = updated.filter((r) => Object.values(r).some((v) => v !== ''));
    onChange(name, filtered);
  };

  const handleFieldChange = (index, fieldName, fieldValue) => {
    let updated = rows.map((row, i) => (i === index ? { ...row, [fieldName]: fieldValue } : row));
    if (!fixedRows && index === rows.length - 1 && fieldValue !== '') {
      updated = [...updated, fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {})];
    }
    setRows(updated);
    
    if (fixedRows) {
      // For fixed rows, send all rows including empty values
      onChange(name, updated);
    } else {
      triggerChange(updated);
    }
  };

  const handleRemove = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    triggerChange(updated);
  };

  return (
    <div className="subform">
      <table className="subform-table">
        <thead>
          <tr>
            {fields.filter(f => f.visible !== false).map((f) => (
              <th 
                key={f.name}
                style={{ 
                  width: f.width || 'auto',
                  minWidth: '50px',

                }}
              >
                {f.label}
              </th>
            ))}
            <th style={{ width: '40px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {/* Visible fields */}
              {fields.filter(f => f.visible !== false).map((f) => (
                <td 
                  key={f.name}
                  style={{ 
                    width: f.width || 'auto',
                    maxWidth: f.width || 'auto',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
   
                  }}
                >
                  {f.type === 'dropdown' ? (
                    <select
                      value={row[f.name] || ''}
                      onChange={(e) => handleFieldChange(idx, f.name, e.target.value)}
                      disabled={disabled || f.disabled}
                      style={{ width: '100%' }}
                    >
                      <option value="">Select...</option>
                      {f.options.map((opt, i) => (
                        <option key={i} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                      {/* Show current value even if not in options (for legacy data) */}
                      {row[f.name] && !f.options.some(opt => opt.value === row[f.name]) && (
                        <option key="current-value" value={row[f.name]}>
                          {/* Try to find a descriptive name, fallback to ID */}
                          {row.item_name || row.name || `ID: ${row[f.name]}`}
                        </option>
                      )}
                    </select>
                  ) : (
                    <input
                      type={f.type}
                      value={row[f.name] || ''}
                      onChange={(e) => handleFieldChange(idx, f.name, e.target.value)}
                      disabled={disabled || f.disabled}
                      step={f.step}
                      min={f.min}
                      max={f.max}
                      style={{ width: '100%', padding: '0px',  }}
                    />
                  )}
                </td>
              ))}
              <td style={{ width: '30px' }}>
                {!fixedRows && !disabled && idx < rows.length - 1 && (
                  <button type="button" onClick={() => handleRemove(idx)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SubForm;
